import React from "react";
import { Link } from "react-router-dom";
import { compose } from "helper/compose";
import { withTranslation, type WithTranslation } from "react-i18next";

import NewPasswordForm from "./NewPasswordForm";
import { withAuthData, type AuthInjectedProps } from "../../../context/AuthContext";

const NewPasswordFormAny = NewPasswordForm as unknown as React.ComponentType<
  Record<string, unknown>
>;

interface FormErrorItem {
  code: string;
  message: string;
}

interface NewPasswordPageOwnProps extends WithTranslation {
  params: { token: string };
}

type NewPasswordPageProps = NewPasswordPageOwnProps & AuthInjectedProps;

function isValidPassword(password: string): boolean {
  const rules = [/^\S{8,}$/, /[a-z]+/, /[A-Z]+/, /\d+/];
  for (const rule of rules) {
    if (password.match(rule) === null) {
      return false;
    }
  }
  return true;
}

function NewPasswordPage({
  t,
  params,
  actions,
  isResetTokenValid,
  newPasswordPageMessage,
}: NewPasswordPageProps) {
  const [formData, setFormData] = React.useState<{
    email: string;
    password: string;
    passwordConfirm: string;
  }>({ email: "", password: "", passwordConfirm: "" });
  const [formError, setFormError] = React.useState<FormErrorItem[] | undefined>(undefined);

  React.useEffect(() => {
    actions.checkResetToken(params.token);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onFormChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const field = event.target.name as "email" | "password" | "passwordConfirm";
    const value = event.target.value;
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const onFormSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const errors: FormErrorItem[] = [];
    if (formData.email === "") {
      errors.push({ code: "MAIL", message: t("password.new.error_mail") });
    }
    if (formData.password === "") {
      errors.push({ code: "PASSWORD", message: t("password.new.error_password") });
    } else if (isValidPassword(formData.password) === false) {
      errors.push({
        code: "PASSWORD_INVALID",
        message: t("password.new.error_password_invalid"),
      });
    } else if (formData.password !== formData.passwordConfirm) {
      errors.push({
        code: "PASSWORD_CONFIRM",
        message: t("password.new.error_password_confirm"),
      });
    }

    setFormError(errors);

    if (errors.length === 0) {
      actions.setNewPassword(formData.email, formData.password, params.token);
    }
  };

  return (
    <div className="Page AuthPage NewPasswordPageShell">
      <div className="Page-content AuthPage-content">
        <div className="AuthPage-header">
          <div className="LogoCompanyWrapper">
            <Link to={BASE_PATH + "/"}>
              <div className="LogoCompany isSpaced isGMC"></div>
            </Link>
          </div>
          <p className="AuthPage-kicker">{t("topbar.platform_label")}</p>
          <h2 className="Page-title isSpaced">{t("password.new.title")}</h2>
          <p className="AuthPage-intro">{t("password.new.info_password")}</p>
        </div>
        {newPasswordPageMessage ? (
          <p className={"Form-message AuthPage-inlineMessage " + newPasswordPageMessage.type}>
            {newPasswordPageMessage.content}
          </p>
        ) : null}
        {isResetTokenValid ? (
          <NewPasswordFormAny
            onChange={onFormChange}
            onSubmit={onFormSubmit}
            formError={formError}
          />
        ) : null}
        <p className="spaced-paragraph AuthPage-footerLink">
          <Link to={BASE_PATH + "/login"} className="AuthPage-secondaryLink">
            {t("password.new.button_back_login")}
          </Link>
        </p>
      </div>
    </div>
  );
}

export default compose(withTranslation(), withAuthData)(NewPasswordPage);
